import { useCallback, useState } from 'react';

import type { MFEs } from '@/components/AppContext';
import type { Module } from '@/types/mfe.types';
import { getDevToolsStorage, applyQueryParamVersions } from '@/components/dev-tools/utils';

export interface MFEsState {
	isLoading: boolean;
	loaded: boolean;
	module: Module | null;
	version: string;
	retryCount: number;
	loadFailed: boolean;
	loadStartedAt: number | null;
}

const mfeState: MFEsState = {
	isLoading: false,
	loaded: false,
	module: null,
	version: '',
	retryCount: 0,
	loadFailed: false,
	loadStartedAt: null
};

// Apply query param versions first, then get storage
// This ensures URL params take precedence over stored values
applyQueryParamVersions();
const savedDevTools = getDevToolsStorage();

const defaultMFEsState = {
	auth: {
		...mfeState,
		version: savedDevTools?.auth.version || ''
	},
	settings: {
		...mfeState,
		version: savedDevTools?.settings.version || ''
	},
	garage: {
		...mfeState,
		version: savedDevTools?.garage.version || ''
	}
};

/**
 * This hook is used to manage the state of the MFE.
 */
const useMFE = () => {
	const [mfes, setMFEs] = useState<Record<MFEs, typeof mfeState>>(defaultMFEsState);

	// Stable identities (setMFEs from useState is stable) so consumers passing these to
	// useDynamicScript don't re-run its load effect on every render. useDynamicScript's
	// fetch guard contains the resulting churn, but a stable setter keeps the effect
	// deps honest and removes wasted re-runs.
	const setMFEsState = useCallback((mfe: MFEs, state: Partial<MFEsState>) => {
		setMFEs((prevMFEs) => ({
			...prevMFEs,
			[mfe]: { ...prevMFEs[mfe], ...state }
		}));
	}, []);

	const setAuthState = useCallback(
		(state: Partial<MFEsState>) => setMFEsState('auth', state),
		[setMFEsState]
	);

	const setSettingsState = useCallback(
		(state: Partial<MFEsState>) => setMFEsState('settings', state),
		[setMFEsState]
	);

	const setGarageState = useCallback(
		(state: Partial<MFEsState>) => setMFEsState('garage', state),
		[setMFEsState]
	);

	return { mfes, setMFEsState, setAuthState, setSettingsState, setGarageState };
};

export default useMFE;
